import { useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { PermissionSet } from '../../../types';
import { Tag } from '../../../store/Tags';
import { getTags } from '../../../Api/Tags/tagApi';
import { getAccountName } from '../../../store/Accounts';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { deriveAccessLevel } from './derivations';
import { DomainMatrix } from './DomainMatrix';
import { TargetedDeniesEditor } from './TargetedDeniesEditor';
import { PermissionSetEditor } from './PermissionSetEditor';

export interface PermissionSetConfigSectionProps {
  permissionSet: PermissionSet;
}

const noop = () => undefined;

const referencedTagIds = (permissionSet: PermissionSet): string[] => {
  const ids = new Set<string>();
  Object.values(permissionSet.rules).forEach((rule) => (rule?.record_scope_tags ?? []).forEach((id) => ids.add(id)));
  return [...ids];
};

export const PermissionSetConfigSection = ({ permissionSet }: PermissionSetConfigSectionProps) => {
  const [editing, setEditing] = useState(false);
  const [resolvedTags, setResolvedTags] = useState<Tag[]>([]);
  const [boundAccountNames, setBoundAccountNames] = useState<string[]>([]);

  useEffect(() => {
    const accountIds = permissionSet.bound_account_ids ?? [];
    if (!accountIds.length) {
      setResolvedTags([]);
      setBoundAccountNames([]);
      return;
    }
    let active = true;
    getTags(accountIds).then(({ data }) => active && setResolvedTags(data ?? []));
    Promise.all(accountIds.map((id) => getAccountName(id))).then(
      (names) => active && setBoundAccountNames(names.filter(Boolean))
    );
    return () => {
      active = false;
    };
  }, [permissionSet]);

  const tagOptions = useMemo<Tag[]>(() => {
    const byId = new Map(resolvedTags.map((tag) => [tag.id, tag]));
    return referencedTagIds(permissionSet).map((id) => byId.get(id) ?? { id, name: id, color: '', accountId: '' });
  }, [permissionSet, resolvedTags]);

  if (editing)
    return (
      <PermissionSetEditor
        mode="edit"
        permissionSet={permissionSet}
        onCancel={() => setEditing(false)}
        onSaved={() => setEditing(false)}
      />
    );

  return (
    <Stack
      spacing={3}
      data-cy="permission-set-config">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center">
        <Typography variant="h6">{permissionSet.name}</Typography>
        <button
          type="button"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          data-cy="edit-permission-set"
          onClick={() => setEditing(true)}>
          Edit
        </button>
      </Stack>

      {permissionSet.description && (
        <Typography
          variant="body2"
          color="text.secondary">
          {permissionSet.description}
        </Typography>
      )}

      {boundAccountNames.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">Field &amp; Tag Source Account</Typography>
          <Typography variant="body2">{boundAccountNames.join(', ')}</Typography>
        </Stack>
      )}

      <Stack spacing={0.5}>
        <Typography variant="subtitle2">Access Level</Typography>
        <Typography variant="body2">{deriveAccessLevel(permissionSet.rules)}</Typography>
      </Stack>

      <Stack spacing={1}>
        <Typography variant="subtitle2">Feature Permissions</Typography>
        <DomainMatrix
          rules={permissionSet.rules}
          tagOptions={tagOptions}
          defaultWrite={false}
          readOnly
          onChange={noop}
        />
      </Stack>

      <Stack spacing={1}>
        <Typography variant="subtitle2">Action Restrictions</Typography>
        <TargetedDeniesEditor
          value={permissionSet.targeted_denies}
          onChange={noop}
          readOnly
        />
      </Stack>
    </Stack>
  );
};
